import { SYP_LITURGICAL_BGCOLORS } from './color.ts';
import { unixTimestamp, utcOffsetSeconds } from './util.ts';

/**
 * A representation of a date.
 */
export class Date {
  /**
   * The number of days since 1970-01-01 (the Unix epoch).
   */
  protected n: bigint;

  /**
   * Create a new date based on the provided year, month, and day.
   */
  public constructor(year: number | bigint | Days, month: number | bigint, day: number | bigint) {
    if (year instanceof Days) {
      this.n = year.n;
    } else {
      assert(month >= 1 && month <= 12, 'Month out-of-bounds');
      assert(daysInMonth(year, month) >= day, '');

      // The algorithm to convert from a civil year/month/day to the number of days that have elapsed
      // since the epoch is taken from here:
      // https://howardhinnant.github.io/date_algorithms.html#days_from_civil
      const m = BigInt(month);
      const y = BigInt(year) - (m <= 2n ? 1n : 0n);
      const d = BigInt(day);
      const era: bigint = (y >= 0n ? y : y - 399n) / 400n;
      const year_of_era = y - era * 400n;
      const day_of_year = (153n * (m > 2n ? m - 3n : m + 9n) + 2n) / 5n + d - 1n;
      const day_of_era = year_of_era * 365n + year_of_era / 4n - year_of_era / 100n + day_of_year;
      this.n = era * 146_097n + day_of_era - 719_468n;
    }
  }

  /**
   * Create a new Date object based on a Unix timestamp.
   */
  public static fromUnixTimestamp(seconds: number | bigint): Date {
    const unix_time = BigInt(seconds);
    return new Date(new Days(unix_time / 86_400n), 0, 0);
  }

  /**
   * Create a new Date object based on a Unix timestamp in microseconds.
   */
  public static fromUnixTimestampMillis(micros: number | bigint): Date {
    const unix_micros = BigInt(micros);
    return new Date(new Days(unix_micros / 86_400_000_000n), 0, 0);
  }

  /**
   * Create a new Date object from a `YYYY-MM-DD` component string.
   */
  public static fromString(date: string): Date {
    let captures = date.match(/^([\d]{4})-([\d]{2})-([\d]{2})$/);
    if (captures === null) {
      throw new Error(`Invalid date: ${date}`);
    }
    return new Date(parseInt(captures[1]), parseInt(captures[2]), parseInt(captures[3]));
  }

  /**
   * Today's date.
   */
  public static today(): Date {
    return Date.fromUnixTimestamp(unixTimestamp() - utcOffsetSeconds());
  }

  /**
   * Return the date of Easter in the Gregorian calendar for the given year.
   */
  public static easter(year: number | bigint): Date {
    if (year < 1583 || year > 9999) {
      throw new Error('Year out of bounds');
    }
    let a = BigInt(year) % 19n;
    let b = BigInt(year) / 100n;
    let c = BigInt(year) % 100n;
    let d = b / 4n;
    let e = b % 4n;
    let f = (b + 8n) / 25n;
    let g = (b - f + 1n) / 3n;
    let h = (19n * a + b - d - g + 15n) % 30n;
    let i = c / 4n;
    let j = c % 4n;
    let k = (32n + 2n * e + 2n * i - h - j) % 7n;
    let l = (a + 11n * h + 22n * k) / 451n;
    let month = (h + k - 7n * l + 114n) / 31n;
    let day = ((h + k - 7n * l + 114n) % 31n) + 1n;
    return new Date(year, month, day);
  }

  /**
   * The year for this date.
   */
  public get year(): number {
    return Number(this.ymd[0]);
  }

  /**
   * The month for this date. Range: `[1, 12]`
   */
  public get month(): number {
    return Number(this.ymd[1]);
  }

  /**
   * The day of the month. Range varies by month; absolute range `[1, 31]`.
   */
  public get day(): number {
    return Number(this.ymd[2]);
  }

  /**
   * The ordinal day of the year. Range: `[1, 366]`
   */
  public get dayOfYear(): number {
    return Number(this.n - new Date(this.year, 1, 1).n + 1n);
  }

  /**
   * The week number of the year, range `[0, 53]`.
   *
   * A new week starts each Sunday. Week 1 begins on the first Sunday of the year;
   * leading days prior to the first Sunday are part of week 0.
   */
  public get week(): number {
    const jan1 = new Date(this.year, 1, 1);
    const first_sunday = jan1.n + (this.n % 7n === 3n ? 0n : 7n) - ((this.n + 4n) % 7n);
    if (this.n < first_sunday) {
      return 0;
    }
    return Number((this.n - first_sunday) / 7n + 1n);
  }

  /**
   * The weekday of this date.
   */
  public get weekday(): Weekday {
    switch ((this.n + 4n) % 7n) {
      case 0n:
        return new Weekday(WeekdayEnum.Sunday);
      case 1n:
      case -6n:
        return new Weekday(WeekdayEnum.Monday);
      case 2n:
      case -5n:
        return new Weekday(WeekdayEnum.Tuesday);
      case 3n:
      case -4n:
        return new Weekday(WeekdayEnum.Wednesday);
      case 4n:
      case -3n:
        return new Weekday(WeekdayEnum.Thursday);
      case 5n:
      case -2n:
        return new Weekday(WeekdayEnum.Friday);
      case 6n:
      case -1n:
        return new Weekday(WeekdayEnum.Saturday);
      default:
        throw new Error('unreachable');
    }
  }

  /**
   * The number of calendar days between this date and `other`.
   *
   * This value is always 0 or greater.
   */
  public daysBetween(other: Date): bigint {
    let dist = this.n - other.n;
    if (dist < 0n) {
      return dist * -1n;
    }
    return dist;
  }

  /**
   * The date that is `days` after or before the current date.
   *
   * A negative `days` argument moves backward; a positive argument moves forward.
   */
  public interval(days: number | bigint): Date {
    return new Date(new Days(this.n + BigInt(days)), 0, 0);
  }

  /**
   * The next Lord's Day.
   *
   * If `lazy` is provided and set to `true`, this will return the unmodified date iff that
   * date is a Sunday.
   */
  public nextSunday(opts?: { lazy?: boolean }): Date {
    let lazy = opts?.lazy ?? false;
    let weekdayNum: number = this.weekday.asNumber;
    if (lazy === true && weekdayNum === 0) {
      return this;
    }
    return new Date(new Days(this.interval(7 - weekdayNum).n), 0, 0);
  }

  /**
   * The previous Lord's Day.
   *
   * If `lazy` is provided and set to `true`, this will return the unmodified date iff that
   * date is a Sunday.
   */
  public prevSunday(opts?: { lazy?: boolean }): Date {
    let lazy = opts?.lazy ?? false;
    let weekdayNum: number = this.weekday.asNumber;
    if (lazy === false && weekdayNum === 0) {
      return new Date(new Days(this.n - 7n), 0, 0);
    }
    return new Date(new Days(this.interval(-weekdayNum).n), 0, 0);
  }

  /**
   * Provide the liturgical year calendar for the liturgical year this date is in.
   */
  public get liturgicalYear(): LiturgicalYear {
    let [y, m, d] = this.ymd;
    if (m === 12n || (m === 11n && d >= 27n)) {
      return LiturgicalYear.get(y + 1n);
    }
    return LiturgicalYear.get(y);
  }
  /**
   * The YYYY-MM-DD string representation for this date.
   */
  public toString(): string {
    return this.format('%Y-%m-%d');
  }

  /**
   * Format the date according to the specified `strftime` conversion string.
   *
   * This _only_ handles date formatting, and is completely ignorant of liturgical dating.
   * Use this method when you need `strftime`. For liturgical date labels, use `label` instead.
   * If you need both, use `label` (which calls `format` under the hood).
   */
  public format(formatStr: string): string {
    // Get the actual year/month/day.
    const [year, month, day] = this.ymd;

    // Initial setup.
    let answer = new FormattedDate();
    let flag = false;
    let padding = Padding.Zero;

    // Begin processing the format string.
    for (const char of formatStr) {
      // If we're not in "flag mode", then the only thing we can possibly to is enter flag mode.
      // Any other character is a literal.
      if (flag === false) {
        if (char === '%') {
          flag = true;
        } else {
          answer.writeLiteral(char);
        }
        continue;
      }

      // We are in flag mode; write the requested character(s), or alter the flag if requested.
      switch (char) {
        // Year
        case 'Y':
          answer.writePadded(year, 4, padding);
          break;
        case 'y':
          answer.writePadded(year % 100n, 2, padding);
          break;
        case 'C':
          answer.writePadded(year / 100n, 2, padding);
          break;
        // Month
        case 'm':
          answer.writePadded(month, 2, padding);
          break;
        case 'b':
        case 'h':
          answer.writeLiteral(new Month(month).abbv);
          break;
        case 'B':
          answer.writeLiteral(new Month(month).name);
          break;
        // Day
        case 'd':
          answer.writePadded(day, 2, padding);
          break;
        // Weekday
        case 'a':
          answer.writeLiteral(this.weekday.abbv);
          break;
        case 'A':
          answer.writeLiteral(this.weekday.name);
          break;
        case 'w':
          answer.writeLiteral(Number(this.weekday.asNumber).toString());
          break;
        case 'u':
          answer.writeLiteral((Number(this.weekday.asNumber) || 7).toString());
          break;
        // Other
        case 'j':
          answer.writePadded(this.dayOfYear, 3, padding);
          break;
        case 'U':
          answer.writePadded(this.week, 2, padding);
          break;
        // Groups
        case 'F':
          answer.writeLiteral(this.format('%Y-%m-%d'));
          break;
        case 'D':
          answer.writeLiteral(this.format('%m/%d/%y'));
          break;
        case 'v':
          answer.writeLiteral(this.format('%d-%b-%Y'));
          break;
        // Control characters
        case 't':
          answer.writeLiteral('\t');
          break;
        case 'n':
          answer.writeLiteral('\n');
          break;
        case '%':
          answer.writeLiteral('%');
          break;
        // Padding alteration.
        case '0':
          padding = Padding.Zero;
          continue;
        case '-':
          padding = Padding.Suppress;
          continue;
        case '_':
          padding = Padding.Space;
          continue;
        default:
          throw new Error('unrecognized format character');
      }
      flag = false;
      padding = Padding.Zero;
    }

    return answer.toString();
  }

  /**
   * Whether this date is equal to another date.
   */
  public equals(other: Date): boolean {
    return this.n === other.n;
  }

  /**
   * Whether this date falls in between two dates.
   * The start is inclusive and the end is exclusive.
   */
  public isBetween(start: Date, end: Date): boolean {
    return this.n >= start.n && this.n < end.n;
  }

  /**
   * Whether it is Sunday.
   */
  public get isSunday(): boolean {
    return this.weekday.asNumber === 0;
  }

  /**
   * The Sunday this many weeks from today.
   *
   * This *always* returns a Sunday if `weeks` is non-zero, even if the date on which this is
   * called isn't a Sunday.
   */
  public weekInterval(weeks: number | bigint): Date {
    let cursor: Date = this;
    let week_counter = BigInt(weeks);
    while (week_counter > 0n) {
      cursor = cursor.nextSunday();
      week_counter -= 1n;
    }
    while (week_counter < 0n) {
      cursor = cursor.prevSunday();
      week_counter += 1n;
    }
    return cursor;
  }

  /**
   * Whether this Sunday is a festival Sunday (meaning `.label('#F')` would be non-empty)
   */
  public get isFestival(): boolean {
    const ly = this.liturgicalYear;
    return (
      this.equals(ly.christmasDay) ||
      this.equals(ly.epiphany) ||
      this.equals(ly.baptismSunday) ||
      this.equals(ly.transfigurationSunday) ||
      this.equals(ly.palmSunday) ||
      this.equals(ly.easter) ||
      this.equals(ly.ascensionSunday) ||
      this.equals(ly.pentecostSunday) ||
      this.equals(ly.trinitySunday) ||
      this.equals(ly.reformationSunday) ||
      this.equals(ly.allSaintsSunday) ||
      this.equals(ly.lastSunday)
    );
  }

  /**
   * The liturgical season.
   */
  public get season(): Season {
    const ly = this.liturgicalYear;
    if (this < ly.christmasDay) {
      return Season.Advent;
    } else if (this < ly.epiphany) {
      return Season.Christmas;
    } else if (this < ly.ashWednesday) {
      return Season.Epiphany;
    } else if (this < ly.easter) {
      return Season.Lent;
    } else if (this < ly.easter.interval(50)) {
      return Season.Easter;
    } else {
      return Season.OrdinaryTime;
    }
  }

  /**
   * The occasion in which this date falls.
   *
   * See the `Occasion` enum for additional information.
   */
  public get occasion(): Occasion | undefined {
    const year = this.year;
    const ly = LiturgicalYear.get(year);
    switch (this.season) {
      case Season.Advent:
        return Occasion.Advent;
      case Season.Christmas:
        return Occasion.Christmas;
      case Season.Epiphany:
        // The week prior to Ash Wednesday should be designated as Transfiguration.
        //
        // For our purposes, we'll start this on the Wednesday prior to Transfiguration Sunday,
        // ending the Tuesday after, so Transfiguration gets one and exactly one week.
        if (this.isBetween(ly.ashWednesday.interval(-7), ly.ashWednesday)) {
          return Occasion.Transfiguration;
        }
        return Occasion.Epiphany;
      case Season.Lent:
        if (this.isBetween(ly.palmSunday, ly.easter)) {
          return Occasion.HolyWeek;
        }
        return Occasion.Lent;
      case Season.Easter:
        if (this.isBetween(ly.ascensionDay, ly.pentecostSunday)) {
          return Occasion.Ascension;
        }
        // The *Day* of Pentecost is actually part of the Easter season, so we check for it here
        // as well as during Ordinary Time.
        else if (this.equals(ly.pentecostSunday)) {
          return Occasion.Pentecost;
        }
        return Occasion.Easter;
      default:
        if (this.isBetween(ly.pentecostSunday, ly.trinitySunday)) {
          return Occasion.Pentecost;
        }
        // The Sunday to Saturday period starting with Trinity Sunday can properly focus
        // Trinitarian hymnody.
        else if (this.isBetween(ly.trinitySunday, ly.trinitySunday.weekInterval(1))) {
          return Occasion.Trinity;
        }
        // October 25-31, regardless of days of the week, will be "Reformation Week".
        // Reformation Sunday will always fall within this week.
        else if (this.isBetween(ly.reformationDay.interval(-7), ly.allSaintsDay)) {
          return Occasion.Reformation;
        }
        // November 1-7, regardless of days of the week, will be "All Saints Week".
        // All Saints Sunday will always fall within this week.
        else if (this.isBetween(ly.allSaintsDay, ly.allSaintsDay.interval(7))) {
          return Occasion.AllSaints;
        }
        // The remainder of the church year is "Fulfillmenttide" (my bogus term), when
        // readings and music focus more on the second coming.
        else if (this.isBetween(ly.allSaintsDay.interval(-7), new Date(year, 11, 27))) {
          return Occasion.Fulfillment;
        }
        return undefined;
    }
  }

  /**
   * A hexadecimal CSS color code for a light background color corresponding to the liturgical
   * color for this date.
   *
   * @param forceMorning Forces the morning color on the two days where the liturgical color shifts
   * at noon (Christmas Eve and Holy Saturday).
   */
  public bgColor(forceMorning = false): string {
    // The precise color shades are based on running the SYP color (#5300BB) through a color wheel
    // and should compliment the SYP branding.
    // prettier-ignore
    const [BLUE, WHITE, GREEN, PURPLE, RED, BLACK] =
      ['#bbe0ff', '#ffffff', '#beffbb', '#d9bbff', '#ffbbbe', '#000000'];
    const ly = LiturgicalYear.get(this.year);
    switch (this.season) {
      case Season.Advent:
        if (!forceMorning && this.equals(LiturgicalYear.get(this.year + 1).christmasEve)) {
          return WHITE;
        }
        return BLUE;
      case Season.Christmas:
        return WHITE;
      case Season.Epiphany:
        return this.equals(ly.epiphany) || this.equals(ly.baptismSunday) || this.equals(ly.transfigurationSunday)
          ? WHITE
          : GREEN;
      case Season.Lent:
        if (this.isBetween(ly.palmSunday, ly.goodFriday)) {
          return RED;
        } else if (this.equals(ly.ashWednesday) || this.equals(ly.goodFriday)) {
          return BLACK;
        } else if (this.equals(ly.goodFriday.interval(1))) {
          return forceMorning ? BLACK : WHITE;
        }
        return PURPLE;
      case Season.Easter:
        return this.equals(ly.pentecostSunday) ? RED : WHITE;
      case Season.OrdinaryTime:
        // prettier-ignore
        return this.equals(ly.trinitySunday) || this.equals(ly.allSaintsDay) ||
               this.equals(ly.allSaintsSunday) || this.equals(ly.lastSunday) ? WHITE :
          this.equals(ly.reformationDay) || this.equals(ly.reformationSunday) ? RED : GREEN;
    }
  }

  /**
   * A liturgical label for this date.
   *
   * Substitutions generally only populate on Sundays, and are blank on all weekdays. The exception
   * to this is `#F`, which can populate on festival days (plus Ash Wednesday) that occur during
   * the week.
   *
   * The format string is a strftime/strptime-like string. The `%` values in `Date.format` are all
   * supported. Additionally, liturgical format options use `#` instead of `%` (to avoid collision)
   * and the following format substitutions are supported. All strings are trimmed of leading or
   * trailing whitespace following substitution.
   *
   * - `#D`: "Sunday", except during Christmas ("Day") if counting single days
   * - `#F`: The festival day, if any
   * - `#N`: The number of the week in the season, or "Day" if a season other than Advent commences
   *         on a Sunday, or "F" for the last Sundays of Epiphany and the Church Year.
   * - `#O`: The ordinal name of the number of the week (e.g. "First", "Second"), or the day of
   *         Christmas during Christmastide
   * - `#P`: The proper preposition for counting Sundays in this season.
   * - `#S`: Season
   *
   * Additionally, the following "macros" are provided, occasionally with optimizations:
   *
   * - `#V`: A verbose representation of the week name:
   *     - Generally maps to "#O #D #P #S"
   *     - Special cases: "Last Sunday of the Church Year"
   *
   * All substitution strings may carry a `-` modifier (e.g. `#-P`) to get a terse variant.
   *
   */
  public label(...format: string[]): string {
    for (let fmt of format) {
      let answer = this.labelSunday(fmt).trim();
      if (answer.length === 0) {
        continue;
      }
      if (answer.includes('  ')) {
        answer = answer.replaceAll(/[ ]{2,}/g, ' ');
      }
      if (answer.includes('%')) {
        return this.format(answer);
      }
      return answer;
    }
    return '';
  }

  private labelSunday(format: string): string {
    const ly = this.liturgicalYear;
    const season = this.season;
    const isSunday = this.isSunday;

    // Handle our special-case macros.
    if (format.includes('#V')) {
      if (this.equals(ly.lastSunday)) {
        format = format.replaceAll('#V', 'Last Sunday of the Church Year');
      } else {
        format = format.replaceAll('#V', '#O #D #P #S');
      }
    }
    if (format.includes('#-V')) {
      if (this.equals(ly.lastSunday)) {
        format = format.replaceAll('#-V', 'Last S. of Church Year');
      } else {
        format = format.replaceAll('#-V', '#-O #-D #-P #-S');
      }
    }

    // If this is not a Sunday, only festival days can populate.
    // Exception: It's fine to populate days of Christmas if we're counting by day and not Sunday.
    let opts = ly.options();
    if (!isSunday && !opts.singleChristmasDays) {
      format = format.replaceAll(/#-?[DNOPSV]/g, '');
    }

    // Handle Christmas first; after that we have uniform handling for #D.
    if (season === Season.Christmas) {
      let ordDay = this.daysBetween(ly.christmasDay);
      let ord = ordDay / 7n + 1n;
      const singleDays = opts?.singleChristmasDays ?? false;
      format = singleDays
        ? format
            .replaceAll('#D', 'Day')
            .replaceAll('#-D', 'D.')
            .replaceAll(/#-?N/g, `${ordDay + 1n}`)
            .replaceAll('#O', ordinal(ordDay + 1n))
            .replaceAll('#-O', terseOrdinal(ordDay + 1n))
        : format
            .replaceAll('#D', 'Sunday')
            .replaceAll('#-D', 'S.')
            .replaceAll(/#-?N/g, ordDay === 0n ? 'Day' : `${ord}`)
            .replaceAll('#O', ordinal(ord))
            .replaceAll('#-O', terseOrdinal(ord));
      return format
        .replaceAll('#F', ordDay === 0n ? 'Christmas Day' : '')
        .replaceAll('#-F', ordDay === 0n ? 'Christmas' : '')
        .replaceAll(/#-?P/g, 'of')
        .replaceAll(/#-?S/g, 'Christmas');
    }

    // We've handled Christmas; if this is not a Sunday, only festival days can populate, for real.
    if (!isSunday) {
      format = format.replaceAll(/#-?[DNOPSV]/g, '');
    }

    // #D is Sunday for all remaining seasons, handle it once.
    format = format.replaceAll('#D', 'Sunday').replaceAll('#-D', 'S.');

    // Handle all remaining seasons.
    if (season === Season.Advent) {
      let ord = this.daysBetween(ly.firstSunday) / 7n + 1n;
      return format
        .replaceAll(/#-?F/g, this.equals(ly.christmasEve) ? 'Christmas Eve' : '')
        .replaceAll(/#-?N/g, `${ord}`)
        .replaceAll('#O', ordinal(ord))
        .replaceAll('#-O', terseOrdinal(ord))
        .replaceAll(/#-?P/g, 'of')
        .replaceAll(/#-?S/g, 'Advent');
    } else if (this.season === Season.Epiphany) {
      const ord_day = this.daysBetween(ly.epiphany);
      const ord = ord_day / 7n + (ly.epiphany.isSunday ? 0n : 1n);

      // Is this a festival day?
      // prettier-ignore
      const [long_festival, short_festival] =
        ord_day === 0n ? ['The Epiphany', 'Epiphany'] :
        this.equals(ly.baptismSunday) ? ['The Baptism of our Lord', 'Baptism of Christ'] :
        this.equals(ly.transfigurationSunday) ? ['Transfiguration Sunday', 'Transfiguration S.'] :
        ['', ''];

      // Actually do the replacement.
      return format
        .replaceAll('#F', long_festival)
        .replaceAll('#-F', short_festival)
        .replaceAll(/#-?N/g, ord_day === 0n ? 'Day' : this.equals(ly.transfigurationSunday) ? 'F' : `${ord}`)
        .replaceAll('#O', ord_day === 0n ? '' : ordinal(ord))
        .replaceAll('#-O', ord_day === 0n ? '' : terseOrdinal(ord))
        .replaceAll('#P', ord_day === 0n ? 'of the' : 'after')
        .replaceAll('#-P', ord_day === 0n ? 'of' : 'a.')
        .replaceAll(/#-?S/g, 'Epiphany');
    } else if (season === Season.Lent) {
      const ord = this.daysBetween(ly.ashWednesday) / 7n + 1n;
      // prettier-ignore
      const holy_day =
        this.equals(ly.ashWednesday) ? 'Ash Wednesday' :
        this.equals(ly.palmSunday) ? 'Palm Sunday' :
        this.equals(ly.maundyThursday) ? 'Maundy Thursday' :
        this.equals(ly.goodFriday) ? 'Good Friday' : '';
      return format
        .replaceAll(/#-?F/g, holy_day)
        .replaceAll(/#-?N/g, `${ord}`)
        .replaceAll('#O', ordinal(ord))
        .replaceAll('#-O', terseOrdinal(ord))
        .replaceAll(/#-?P/g, 'in')
        .replaceAll(/#-?S/g, 'Lent');
    } else if (season === Season.Easter) {
      const ord = this.daysBetween(ly.easter) / 7n + 1n;
      // prettier-ignore
      const [short_festival, long_festival] =
        (this.equals(ly.easter)) ? ['Resurrection S.', 'The Resurrection of our Lord'] :
        (this.equals(ly.ascensionDay)) ? ['Ascension', 'The Ascension of our Lord'] :
        (this.equals(ly.ascensionSunday)) ? ['Ascension S.', 'Ascension Sunday'] :
        (this.equals(ly.pentecostSunday)) ? ['Pentecost S.', 'The Day of Pentecost'] : ['', ''];
      return format
        .replaceAll('#F', long_festival)
        .replaceAll('#-F', short_festival)
        .replaceAll(/#-?N/g, `${ord}`)
        .replaceAll('#O', ordinal(ord))
        .replaceAll('#-O', terseOrdinal(ord))
        .replaceAll(/#-?P/g, 'of')
        .replaceAll(/#-?S/g, 'Easter');
    } else {
      const root = opts.countOrdinarySundaysFrom === 'Trinity' ? ly.trinitySunday : ly.pentecostSunday;
      const ord = this.daysBetween(root) / 7n;
      const is_last_sunday = this.equals(ly.lastSunday);
      // prettier-ignore
      const [short_festival, long_festival] =
        (this.equals(ly.trinitySunday)) ? ['Trinity S.', 'The Holy Trinity'] :
        (this.equals(ly.reformationDay)) ? ['Reformation Day', 'Reformation Day'] :
        (this.equals(ly.reformationSunday)) ? ['Reformation S.', 'Reformation Sunday'] :
        (this.equals(ly.allSaintsDay)) ? ['All Saints Day', 'All Saints Day'] :
        (this.equals(ly.allSaintsSunday)) ? ['All Saints S.', 'All Saints Sunday'] :
        (is_last_sunday) ? ['Fulfillment S.', 'The Sunday of the Fulfillment'] : ['', ''];
      return format
        .replaceAll('#F', long_festival)
        .replaceAll('#-F', short_festival)
        .replaceAll(/#-?N/g, is_last_sunday ? 'F' : ord === 0n ? 'Day' : `${ord}`)
        .replaceAll('#O', ordinal(ord))
        .replaceAll('#-O', terseOrdinal(ord))
        .replaceAll('#P', ord === 0n ? 'of the' : 'after')
        .replaceAll('#P', ord === 0n ? 'of the' : 'a.')
        .replaceAll(/#-?S/g, opts.countOrdinarySundaysFrom);
    }
  }

  /**
   * The year, month, and day for this date.
   */
  private get ymd(): [bigint, bigint, bigint] {
    // The algorithm to convert from a civil year/month/day to the number of days that have elapsed
    // since the epoch is taken from here:
    // https://howardhinnant.github.io/date_algorithms.html#civil_from_days
    const shifted = this.n + 719_468n; // Days from March 1, 0 A.D.
    const era = (shifted >= 0 ? shifted : shifted - 146_096n) / 146_097n;
    const doe = shifted - era * 146_097n; // day of era: [0, 146_097)
    const year_of_era = (doe - doe / 1_460n + doe / 36_524n - doe / 146_096n) / 365n;
    const year = year_of_era + era * 400n;
    const day_of_year = doe - (365n * year_of_era + year_of_era / 4n - year_of_era / 100n);
    const mp = (5n * day_of_year + 2n) / 153n;
    const day = day_of_year - (153n * mp + 2n) / 5n + 1n;
    const month = mp < 10n ? mp + 3n : mp - 9n;
    return [year + (month <= 2n ? 1n : 0n), month, day];
  }
}

/**
 * A representation of a weekday.
 */
export class Weekday {
  private n: WeekdayEnum;

  constructor(n: WeekdayEnum) {
    this.n = n;
  }

  /**
   * The name of the weekday.
   */
  public get name(): string {
    switch (this.n) {
      case WeekdayEnum.Sunday:
        return 'Sunday';
      case WeekdayEnum.Monday:
        return 'Monday';
      case WeekdayEnum.Tuesday:
        return 'Tuesday';
      case WeekdayEnum.Wednesday:
        return 'Wednesday';
      case WeekdayEnum.Thursday:
        return 'Thursday';
      case WeekdayEnum.Friday:
        return 'Friday';
      case WeekdayEnum.Saturday:
        return 'Saturday';
    }
  }

  /**
   * The three-letter abbrevation for this weekday.
   */
  public get abbv(): string {
    return this.name.slice(0, 3);
  }

  /**
   * The weekday number (using 0 for Sunday).
   */
  public get asNumber(): number {
    return this.n as number;
  }

  /**
   * The canonical string representation for a weekday.
   */
  public toString(): string {
    return this.name;
  }
}

enum WeekdayEnum {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

/**
 * A representation of a month.
 */
class Month {
  private n: bigint;

  constructor(n: bigint) {
    this.n = n;
  }

  /**
   * The name of the month.
   */
  public get name(): string {
    switch (this.n) {
      case 1n:
        return 'January';
      case 2n:
        return 'February';
      case 3n:
        return 'March';
      case 4n:
        return 'April';
      case 5n:
        return 'May';
      case 6n:
        return 'June';
      case 7n:
        return 'July';
      case 8n:
        return 'August';
      case 9n:
        return 'September';
      case 10n:
        return 'October';
      case 11n:
        return 'November';
      case 12n:
        return 'December';
      default:
        throw new Error('unreachable');
    }
  }

  /**
   * The three-letter abbreviation of the month.
   */
  public get abbv(): string {
    return this.name.slice(0, 3);
  }
}

/**
 * A representation of potential padding options.
 *
 * Used in `Date.format`, and not exported.
 */
enum Padding {
  Zero,
  Suppress,
  Space,
}

/**
 * Complain if a condition is not met.
 */
function assert(expr: boolean, message: string) {
  if (!expr) {
    console.error(message);
    throw new Error(message);
  }
}

/**
 * A formatted date buffer.
 */
class FormattedDate {
  inner: string;

  constructor() {
    this.inner = '';
  }

  writeLiteral(literal: string) {
    this.inner += literal;
  }

  writePadded(num: bigint | number, target: number, padding: Padding) {
    let answer = num.toString();
    if (padding === Padding.Suppress) {
      this.inner += answer;
      return;
    }
    const pad_char = padding === Padding.Zero ? '0' : ' ';
    while (answer.length < target) {
      answer = pad_char + answer;
    }
    this.inner += answer;
  }

  toString(): string {
    return this.inner;
  }
}

/**
 * Options understood by the liturgical label formatter.
 */
export class LiturgicalYearOptions {
  public readonly backgroundColors: LiturgicalColors;
  public readonly countOrdinarySundaysFrom: 'Pentecost' | 'Trinity';
  public readonly singleChristmasDays: boolean;

  /**
   * Construct the class from a JSON object.
   */
  constructor(opts: {
    backgroundColors: LiturgicalColors;
    countOrdinarySundaysFrom?: 'Pentecost' | 'Trinity';
    singleChristmasDays?: boolean;
  }) {
    this.backgroundColors = opts.backgroundColors;
    this.countOrdinarySundaysFrom = opts.countOrdinarySundaysFrom ?? 'Pentecost';
    this.singleChristmasDays = opts.singleChristmasDays ?? false;
  }
}

/**
 * Static containing all liturgical years that have been generated, for cache reuse.
 */
const LITURGICAL_YEARS = {
  options: new LiturgicalYearOptions({ backgroundColors: SYP_LITURGICAL_BGCOLORS }),
  cache: new Map<bigint, LiturgicalYear>(),
};

/**
 * A representation of the western liturgical calendar for a given year.
 */
export class LiturgicalYear {
  /**
   * The liturgical year.
   *
   * This is the Gregorian calendar year in which Easter falls.
   * (In other words, for the 2024-2025 liturgical year, this value would be 2025.)
   */
  private year: bigint;

  /**
   * A cache object to avoid repeatedly calculating Christmas and Easter.
   */
  private cache: Cache;

  /**
   * Create the liturgical calendar object for a given liturgical year.
   *
   * The `year` value is the Gregorian calendar year in which this liturgical year's Easter falls.
   * In other words, for the 2024-2025 liturgical year, this value is 2025.
   */
  private constructor(year: number | bigint) {
    this.year = BigInt(year);
    this.cache = new Cache(this.year);
  }

  /**
   * Retrieve the liturgical year object for the given year.
   *
   * Liturgical years are singletons; repeated calls for the same year will return the same shared,
   * unmodifiable object. Individual liturgical years cache certain data internally to speed up
   * calculations.
   */
  public static get(year: number | bigint): LiturgicalYear {
    const yr = BigInt(year);
    if (!LITURGICAL_YEARS.cache.has(yr)) {
      LITURGICAL_YEARS.cache.set(yr, new LiturgicalYear(yr));
    }
    return LITURGICAL_YEARS.cache.get(yr)!;
  }

  /**
   * The current options for liturgical year rendering.
   */
  public options(): LiturgicalYearOptions {
    return LITURGICAL_YEARS.options;
  }

  /**
   * The lectionary cycle year for the current liturgical year.
   */
  public get threeYearLectionaryCycle(): 'A' | 'B' | 'C' {
    switch ((this.year % 3n) as 0n | 1n | 2n) {
      case 0n:
        return 'C';
      case 1n:
        return 'A';
      case 2n:
        return 'B';
    }
  }

  /**
   * The first Sunday of Advent / the church year
   */
  public get firstSunday(): Date {
    return new Date(this.year - 1n, 11, 27).nextSunday({ lazy: true });
  }

  /**
   * The date of Christmas Eve.
   */
  public get christmasEve(): Date {
    return this.cache.christmas.interval(-1);
  }

  /**
   * The date of Christmas Day.
   */
  public get christmasDay(): Date {
    return this.cache.christmas;
  }

  /**
   * The date of Epiphany.
   */
  public get epiphany(): Date {
    return this.cache.christmas.interval(12);
  }

  /**
   * The date marking the baptism of Christ (the first Sunday after the Epiphany)
   */
  public get baptismSunday(): Date {
    return this.epiphany.nextSunday();
  }

  /**
   * The date of Transfiguration Sunday during this liturgical year.
   */
  public get transfigurationSunday(): Date {
    return this.cache.easter.interval(-49);
  }

  /**
   * The date of Ash Wednesday during this liturgical year.
   */
  public get ashWednesday(): Date {
    // Lent is forty days EXCLUDING SUNDAYS (because every Sunday is a "little Easter").
    // Thus, Ash Wednesday occurs 46 calendar days prior to Easter.
    return this.cache.easter.interval(-46);
  }

  /**
   * The date of Palm Sunday during this liturgical year.
   */
  public get palmSunday(): Date {
    return this.cache.easter.interval(-7);
  }

  /**
   * The date of Maundy Thursday during this liturgical year.
   */
  public get maundyThursday(): Date {
    return this.cache.easter.interval(-3);
  }

  /**
   * The date of Good Friday during this liturgical year.
   */
  public get goodFriday(): Date {
    return this.cache.easter.interval(-2);
  }

  /**
   * The date of Easter during this liturgical year.
   */
  public get easter(): Date {
    return this.cache.easter;
  }

  /**
   * The date of Ascension Day (Thursday) during this liturgical year.
   */
  public get ascensionDay(): Date {
    // Ascension Thursday is "the fourtieth day", not 40 days after.
    return this.cache.easter.interval(39);
  }

  /**
   * The date of Ascension Sunday during this liturgical year.
   */
  public get ascensionSunday(): Date {
    return this.cache.easter.interval(42);
  }

  /**
   * The date of Pentecost Sunday during this liturgical year.
   */
  public get pentecostSunday(): Date {
    // Pentecost Sunday is "the fiftieth day", not 50 days after.
    return this.cache.easter.interval(49);
  }

  /**
   * The date of Trinity Sunday during this liturgical year.
   */
  public get trinitySunday(): Date {
    return this.cache.easter.interval(56);
  }

  /**
   * The date of Reformation Day
   */
  public get reformationDay(): Date {
    return new Date(this.year, 10, 31);
  }

  /**
   * The date of All Saints Day
   */
  public get allSaintsDay(): Date {
    return new Date(this.year, 11, 1);
  }

  /**
   * The date of Reformation Sunday during this liturgical year.
   */
  public get reformationSunday(): Date {
    return this.reformationDay.prevSunday({ lazy: true });
  }

  /**
   * The date of All Saints' Sunday during this liturgical year.
   */
  public get allSaintsSunday(): Date {
    return this.allSaintsDay.nextSunday({ lazy: true });
  }

  /**
   * THe date of the last Sunday of the church year.
   */
  public get lastSunday(): Date {
    return new Date(this.year, 11, 26).prevSunday({ lazy: true });
  }
}

export interface LiturgicalColors {
  blue: string;
  white: string;
  green: string;
  purple: string;
  red: string;
  black: string;
}

/**
 * The liturgical seasons.
 */
export enum Season {
  Advent = 'Advent',
  Christmas = 'Christmas',
  Epiphany = 'Epiphany',
  Lent = 'Lent',
  Easter = 'Easter',
  OrdinaryTime = 'Ordinary Time',
}

/**
 * Liturgical occasions refer either to seasons or festivities within seasons.
 *
 * Liturgical seasons have a certain level of subtlety that we're attempting to properly capture.
 * First, there are the six "base seasons": Advent, Christmas, Epiphany, Lent, Easter, and the
 * Time of the Church (also called Ordinary Time). Each and every date belongs to one and exactly
 * one of these seasons, which rotate in order and are anchored against the two most significant
 * festivals (Christmas Day and Easter Day).
 *
 * However, there are also festival periods within many seasons. The clearest cut of these would
 * be Holy Week at the end of Lent, Ascensiontide (days 40-49 of Easter), or Hallowtide (itself
 * made more complex in Reformation traditions). For our purposes, we're going to treat these as
 * "at most one" -- meaning that a date may fall in a festival period, but doesn't necessarily.
 * The smaller festivals take precedence over the encapsulating season.
 *
 * The representation here has a few limitations which may need to be addressed in the future as
 * additional churches are added:
 *
 * - There are occasions included here that are not observed by all churches (e.g. Reformation for
 *   Roman Catholics and most Anglicans, or All Saints for most calendar-observant Baptists).
 * - This enum doesn't have any concept of Pre-Lent.
 * - There are lesser feasts such as saints' days which, for churches that observe them, limit
 *   their observance to years when the date falls on a Sunday.
 */
export enum Occasion {
  Advent = 'Advent',
  Christmas = 'Christmas',
  Epiphany = 'Epiphany',
  Transfiguration = 'Transfiguration',
  Lent = 'Lent',
  HolyWeek = 'Holy Week',
  Easter = 'Easter',
  Ascension = 'Ascension',
  Pentecost = 'Pentecost',
  Trinity = 'Trinity',
  Reformation = 'Reformation',
  AllSaints = 'All Saints',
  Fulfillment = 'Fulfillment',
}

// -- Module-private Utilities --------------------------------------------------------------------

/**
 * A cache that holds Christmas and Easter to prevent having to calculate them repeatedly.
 */
class Cache {
  private year: bigint;
  private christmas_?: Date;
  private easter_?: Date;

  public constructor(year: bigint) {
    this.year = year;
  }

  public get christmas(): Date {
    if (this.christmas_ === undefined) {
      this.christmas_ = new Date(this.year - 1n, 12, 25);
    }
    return this.christmas_;
  }

  public get easter(): Date {
    if (this.easter_ === undefined) {
      this.easter_ = Date.easter(this.year) as Date;
    }
    return this.easter_;
  }
}

/**
 * Convert a number to its English counting ordinal.
 */
const ordinal = (n: bigint): string => {
  if (n < 0n || n > 29n) {
    throw new Error(`Unsupported interval: ${n}`);
  }
  switch (n) {
    case 0n:
      return '';
    case 1n:
      return 'First';
    case 2n:
      return 'Second';
    case 3n:
      return 'Third';
    case 4n:
      return 'Fourth';
    case 5n:
      return 'Fifth';
    case 6n:
      return 'Sixth';
    case 7n:
      return 'Seventh';
    case 8n:
      return 'Eighth';
    case 9n:
      return 'Ninth';
    case 10n:
      return 'Tenth';
    case 11n:
      return 'Eleventh';
    case 12n:
      return 'Twelfth';
    case 13n:
      return 'Thirteenth';
    case 14n:
      return 'Fourteenth';
    case 15n:
      return 'Fifteenth';
    case 16n:
      return 'Sixteenth';
    case 17n:
      return 'Seventeenth';
    case 18n:
      return 'Eighteenth';
    case 19n:
      return 'Nineteenth';
    case 20n:
      return 'Twentieth';
    default:
      return `Twenty-${ordinal(n - 20n).toLowerCase()}`;
  }
};

const terseOrdinal = (n: bigint): string => {
  // We don't need to care about 111th, 112th, etc. because we'll never go above 30.
  if (n / 10n === 1n || n % 10n === 0n || n % 10n > 3n) {
    return `${n}th`;
  } else if (n % 10n === 1n) {
    return `${n}st`;
  } else if (n % 10n === 2n) {
    return `${n}nd`;
  } else {
    // n % 10n === 3n
    return `${n}rd`;
  }
};

/**
 * Unexported `Days` class exists to be an disambiguation argument for the Date class.
 */
class Days {
  public n: bigint;

  public constructor(n: bigint) {
    this.n = n;
  }
}

/**
 * Whether or not the provided Gregorian year is a leap year.
 */
const isLeapYear = (year: number | bigint): boolean => {
  const y = BigInt(year);
  return y % 4n === 0n && (y % 100n !== 0n || y % 400n === 0n);
};

/**
 * The number of days in the given Gregorian year and month.
 */
const daysInMonth = (year: number | bigint, month: number | bigint): bigint => {
  if (isLeapYear(year)) {
    return DAYS_IN_MONTH_LY[Number(month) - 1];
  } else {
    return DAYS_IN_MONTH[Number(month) - 1];
  }
};

const DAYS_IN_MONTH = [31n, 28n, 31n, 30n, 31n, 30n, 31n, 31n, 30n, 31n, 30n, 31n];
const DAYS_IN_MONTH_LY = [31n, 29n, 31n, 30n, 31n, 30n, 31n, 31n, 30n, 31n, 30n, 31n];
